import { mode } from '@chakra-ui/theme-tools';

const getColor = (props, lightShade = 500, darkShade = 300) => {
  const scheme = props.colorScheme || 'brand';
  return mode(`${scheme}.${lightShade}`, `${scheme}.${darkShade}`)(props);
};

export const buttonStyles = {
  components: {
    Button: {
      baseStyle: {
        borderRadius: '14px',
        fontWeight: '600',
        letterSpacing: '0.01em',
        boxShadow: 'none',
        transition: 'all 0.2s ease',
        boxSizing: 'border-box',
        _focusVisible: {
          boxShadow: mode(
            '0 0 0 3px rgba(57, 101, 255, 0.28)',
            '0 0 0 3px rgba(138, 180, 255, 0.35)',
          ),
        },
        _disabled: {
          opacity: 0.65,
          cursor: 'not-allowed',
          boxShadow: 'none',
          transform: 'none',
        },
        _active: {
          transform: 'translateY(0)',
          boxShadow: 'none',
        },
      },
      sizes: {
        xs: {
          h: '30px',
          minW: '30px',
          fontSize: 'xs',
          px: 3,
        },
        sm: {
          h: '36px',
          minW: '36px',
          fontSize: 'sm',
          px: 4,
        },
        md: {
          h: '44px',
          minW: '44px',
          fontSize: 'sm',
          px: 5,
        },
        lg: {
          h: '50px',
          minW: '50px',
          fontSize: 'md',
          px: 6,
        },
      },
      variants: {
        solid: (props) => ({
          _hover: {
            transform: 'translateY(-1px)',
            boxShadow: mode(
              '0 10px 24px rgba(15, 23, 42, 0.15)',
              '0 10px 24px rgba(0, 0, 0, 0.42)',
            )(props),
          },
        }),
        outline: (props) => ({
          borderRadius: '14px',
          borderWidth: '1px',
          borderColor: mode('secondaryGray.400', 'whiteAlpha.300')(props),
          bg: mode('white', 'transparent')(props),
          color: mode('navy.700', 'white')(props),
          _hover: {
            bg: mode('secondaryGray.100', 'whiteAlpha.200')(props),
            transform: 'translateY(-1px)',
            boxShadow: mode(
              '0 8px 18px rgba(15, 23, 42, 0.08)',
              '0 8px 18px rgba(0, 0, 0, 0.36)',
            )(props),
          },
        }),
        brand: (props) => ({
          bg: mode('brand.500', 'brand.400')(props),
          color: 'white',
          borderRadius: '14px',
          _focus: {
            bg: mode('brand.500', 'brand.400')(props),
          },
          _active: {
            bg: mode('brand.500', 'brand.400')(props),
          },
          _hover: {
            bg: mode('brand.600', 'brand.400')(props),
            transform: 'translateY(-1px)',
            boxShadow: mode(
              '0 10px 24px rgba(31, 63, 184, 0.28)',
              '0 10px 24px rgba(0, 0, 0, 0.45)',
            )(props),
          },
        }),
        darkBrand: (props) => ({
          bg: mode('brand.900', 'brand.400')(props),
          color: 'white',
          borderRadius: '14px',
          _focus: {
            bg: mode('brand.900', 'brand.400')(props),
          },
          _active: {
            bg: mode('brand.900', 'brand.400')(props),
          },
          _hover: {
            bg: mode('brand.800', 'brand.400')(props),
          },
        }),
        lightBrand: (props) => ({
          bg: mode('#F2EFFF', 'whiteAlpha.100')(props),
          color: mode('brand.500', 'white')(props),
          borderRadius: '14px',
          _focus: {
            bg: mode('#F2EFFF', 'whiteAlpha.100')(props),
          },
          _active: {
            bg: mode('secondaryGray.300', 'whiteAlpha.100')(props),
          },
          _hover: {
            bg: mode('secondaryGray.400', 'whiteAlpha.200')(props),
          },
        }),
        light: (props) => ({
          bg: mode('secondaryGray.300', 'whiteAlpha.100')(props),
          color: mode('secondaryGray.900', 'white')(props),
          borderRadius: '14px',
          _focus: {
            bg: mode('secondaryGray.300', 'whiteAlpha.100')(props),
          },
          _active: {
            bg: mode('secondaryGray.300', 'whiteAlpha.100')(props),
          },
          _hover: {
            bg: mode('secondaryGray.400', 'whiteAlpha.200')(props),
          },
        }),
        action: (props) => ({
          fontWeight: '600',
          borderRadius: '14px',
          bg: mode('secondaryGray.300', 'brand.400')(props),
          color: mode('brand.500', 'white')(props),
          _focus: {
            bg: mode('secondaryGray.300', 'brand.400')(props),
          },
          _active: { bg: mode('secondaryGray.300', 'brand.400')(props) },
          _hover: {
            bg: mode('secondaryGray.200', 'brand.400')(props),
          },
        }),
        setup: (props) => ({
          fontWeight: '600',
          borderRadius: '14px',
          bg: mode('transparent', 'brand.400')(props),
          border: mode('1px solid', '0px solid')(props),
          borderColor: mode('secondaryGray.400', 'transparent')(props),
          color: mode('secondaryGray.900', 'white')(props),
          _focus: {
            bg: mode('transparent', 'brand.400')(props),
          },
          _active: { bg: mode('transparent', 'brand.400')(props) },
          _hover: {
            bg: mode('secondaryGray.100', 'brand.400')(props),
          },
        }),
        ghost: (props) => ({
          borderRadius: '14px',
          color: mode('navy.700', 'white')(props),
          _hover: {
            bg: mode('secondaryGray.200', 'whiteAlpha.200')(props),
            transform: 'translateY(-1px)',
            boxShadow: mode(
              '0 8px 18px rgba(15, 23, 42, 0.08)',
              '0 8px 18px rgba(0, 0, 0, 0.36)',
            )(props),
          },
          _active: {
            bg: mode('secondaryGray.300', 'whiteAlpha.300')(props),
          },
        }),
        link: (props) => ({
          h: 'auto',
          minW: 'unset',
          px: 0,
          borderRadius: '0',
          boxShadow: 'none',
          color: getColor(props),
          textDecoration: 'none',
          _hover: {
            color: getColor(props, 600, 200),
            textDecoration: 'underline',
            bg: 'transparent',
            transform: 'none',
            boxShadow: 'none',
          },
          _active: {
            color: getColor(props, 700, 100),
            bg: 'transparent',
            transform: 'none',
            boxShadow: 'none',
          },
        }),
      },
      defaultProps: {
        size: 'md',
      },
    },
  },
};
